// Retime mapping utilities.
// Runtime math operates in local seconds, while persisted points are normalized.
// `localizeRetimePoints()` bridges normalized storage <-> local mapping math.
import { localizeRetimePoints } from './retimePoints'

function normalizePoints(points, clipLength) {
  return localizeRetimePoints(points, clipLength)
}

const pointAffects = (point) => (point?.affects === 'right' ? 'right' : 'left')

function stepSegmentsFromPoints(clipLength, points) {
  const pts = normalizePoints(points, clipLength)
  if (!pts.length) return [{ t0: 0, t1: clipLength, speed: 1 }]

  const boundaries = [0, ...pts.map((p) => p.t), clipLength]
  const segs = []
  for (let i = 0; i < boundaries.length - 1; i++) {
    const t0 = boundaries[i]
    const t1 = boundaries[i + 1]
    const leftPoint = i > 0 ? pts[i - 1] : null
    const rightPoint = i < pts.length ? pts[i] : null

    // Priority:
    // 1) Right boundary point affecting left segment
    // 2) Left boundary point affecting right segment
    // 3) default 1x baseline
    let speed = 1
    if (rightPoint && pointAffects(rightPoint) === 'left') {
      speed = Number(rightPoint.speed) > 0 ? Number(rightPoint.speed) : 1
    } else if (leftPoint && pointAffects(leftPoint) === 'right') {
      speed = Number(leftPoint.speed) > 0 ? Number(leftPoint.speed) : 1
    }

    segs.push({ t0, t1, speed })
  }
  return segs
}

function segmentsFromPoints(clipLength, points) {
  const pts = normalizePoints(points, clipLength)
  if (!pts.length) return [{ t0: 0, t1: clipLength, s0: 1, s1: 1 }]
  const segs = []
  // head
  if (pts[0].t > 0) segs.push({ t0: 0, t1: pts[0].t, s0: pts[0].speed, s1: pts[0].speed })
  // middle
  for (let i = 0; i < pts.length - 1; i++) {
    segs.push({ t0: pts[i].t, t1: pts[i + 1].t, s0: pts[i].speed, s1: pts[i + 1].speed })
  }
  // tail
  if (pts[pts.length - 1].t < clipLength) {
    const last = pts[pts.length - 1]
    segs.push({ t0: last.t, t1: clipLength, s0: last.speed, s1: last.speed })
  }
  return segs
}

function areaLinear(s0, s1, L, x) {
  // integral of linear speed from 0..x within segment length L
  if (L <= 1e-9 || Math.abs(s1 - s0) <= 1e-9) return s0 * x
  const k = (s1 - s0) / L
  return s0 * x + 0.5 * k * x * x
}

export function mapLocalToMedia(localTime, clipLength, points, mode = 'linear') {
  const lt = Math.max(0, Math.min(clipLength, Number(localTime) || 0))
  const pts = normalizePoints(points, clipLength)
  if (!pts.length) return lt
  if (mode === 'step') {
    const segs = stepSegmentsFromPoints(clipLength, pts)
    let media = 0
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]
      if (lt <= seg.t0) return media
      const L = Math.max(0, seg.t1 - seg.t0)
      const x = Math.min(L, Math.max(0, lt - seg.t0))
      media += x * seg.speed
      if (lt <= seg.t1) return media
    }
    return media
  }
  // linear ramp mapping
  const segs = segmentsFromPoints(clipLength, pts)
  let media = 0
  for (let i = 0; i < segs.length; i++) {
    const { t0, t1, s0, s1 } = segs[i]
    if (lt <= t0) return media
    const L = t1 - t0
    const x = Math.min(L, Math.max(0, lt - t0))
    media += areaLinear(s0, s1, L, x)
    if (lt <= t1) return media
  }
  return media
}

export function mapMediaToLocal(mediaTime, clipLength, points, mode = 'linear') {
  const pts = normalizePoints(points, clipLength)
  if (!pts.length) return Math.max(0, Math.min(clipLength, Number(mediaTime) || 0))
  if (mode === 'step') {
    const totalMedia = mapLocalToMedia(clipLength, clipLength, pts, 'step')
    let mt = Math.max(0, Math.min(totalMedia, Number(mediaTime) || 0))
    const segs = stepSegmentsFromPoints(clipLength, pts)
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]
      const segLocal = Math.max(0, seg.t1 - seg.t0)
      const segMedia = segLocal * Math.max(1e-6, seg.speed)
      if (mt <= segMedia) {
        const dtLocal = mt / Math.max(1e-6, seg.speed)
        return Math.max(0, Math.min(clipLength, seg.t0 + dtLocal))
      }
      mt -= segMedia
    }
    return clipLength
  }
  const segs = segmentsFromPoints(clipLength, pts)
  const totalMedia = mapLocalToMedia(clipLength, clipLength, pts, 'linear')
  let mt = Math.max(0, Math.min(totalMedia, Number(mediaTime) || 0))
  for (let i = 0; i < segs.length; i++) {
    const { t0, t1, s0, s1 } = segs[i]
    const L = t1 - t0
    const segArea = areaLinear(s0, s1, L, L)
    if (mt <= segArea) {
      // solve for x in areaLinear(s0,s1,L,x) = mt
      if (L <= 1e-9 || Math.abs(s1 - s0) <= 1e-9) {
        const x = mt / Math.max(1e-6, s0)
        return Math.max(0, Math.min(clipLength, t0 + x))
      }
      const k = (s1 - s0) / L
      // s0*x + 0.5*k*x^2 = mt => 0.5*k*x^2 + s0*x - mt = 0
      const A = 0.5 * k
      const B = s0
      const C = -mt
      const D = B * B - 4 * A * C
      const sqrtD = D > 0 ? Math.sqrt(D) : 0
      const x = Math.max(0, Math.min(L, (-B + sqrtD) / (2 * A)))
      return Math.max(0, Math.min(clipLength, t0 + x))
    }
    mt -= segArea
  }
  return clipLength
}

export function speedAtLocal(localTime, clipLength, points, mode = 'linear') {
  const lt = Math.max(0, Math.min(clipLength, Number(localTime) || 0))
  const pts = normalizePoints(points, clipLength)
  if (!pts.length) return 1
  if (mode === 'step') {
    const eps = 1e-6
    // At an interior boundary, expose the boundary point's own speed so
    // instantaneous rate follows point-drag semantics consistently.
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]
      if (p.t <= 0 || p.t >= clipLength) continue
      if (Math.abs(lt - p.t) <= eps) {
        const sp = Number(p.speed)
        return Number.isFinite(sp) && sp > 0 ? sp : 1
      }
    }
    const segs = stepSegmentsFromPoints(clipLength, pts)
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]
      const isLast = i === segs.length - 1
      if (lt >= seg.t0 && (lt < seg.t1 || (isLast && lt <= seg.t1))) return seg.speed
    }
    return segs[segs.length - 1]?.speed || 1
  }
  const segs = segmentsFromPoints(clipLength, pts)
  for (let i = 0; i < segs.length; i++) {
    const { t0, t1, s0, s1 } = segs[i]
    if (lt >= t0 && lt <= t1) {
      const L = t1 - t0
      if (L <= 1e-9 || Math.abs(s1 - s0) <= 1e-9) return s0
      const k = (s1 - s0) / L
      return s0 + k * (lt - t0)
    }
  }
  return pts[pts.length - 1].speed || 1
}

